import React, { useState, useEffect, useRef } from "react";
import "./NavbarStyle.css";
import { Link } from "react-scroll";
import {
  FaGithub,
  FaLinkedin,
  FaHome,
  FaUser,
  FaProjectDiagram,
  FaGraduationCap,
  FaTrophy,
  FaTools,
  FaEnvelope,
  FaBriefcase,
} from "react-icons/fa";

const XIcon = ({ className, size = 25, style }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function Navbar() {
  const [isExpanded, setIsExpanded]   = useState(false);
  const [isDesktop, setIsDesktop]     = useState(true);
  const [tabVisible, setTabVisible]   = useState(true);

  const lastScrollY  = useRef(0);
  const scrollTimer  = useRef(null);
  const ticking      = useRef(false);

  /* ── Detect desktop vs mobile ── */
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth > 750 && window.innerHeight > 550);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ── Smart scroll: hide on down, show on up, auto-show on stop ── */
  useEffect(() => {
    if (isDesktop) return;                       // only mobile

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta    = currentY - lastScrollY.current;

        if (delta > 6 && currentY > 80) {
          // Scrolling DOWN → hide
          setTabVisible(false);
        } else if (delta < -4) {
          // Scrolling UP → show immediately
          setTabVisible(true);
        }

        lastScrollY.current = currentY;
        ticking.current     = false;

        // Auto-show after user stops scrolling for 800ms
        clearTimeout(scrollTimer.current);
        scrollTimer.current = setTimeout(() => setTabVisible(true), 800);
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(scrollTimer.current);
    };
  }, [isDesktop]);

  const handleMouseEnter = () => isDesktop && setIsExpanded(true);
  const handleMouseLeave = () => isDesktop && setIsExpanded(false);

  const navItems = [
    { to: "home",       label: "Home",       icon: FaHome },
    { to: "about",      label: "About",      icon: FaUser },
    { to: "experience", label: "Experience", icon: FaBriefcase },
    { to: "project",    label: "Projects",   icon: FaProjectDiagram },
    { to: "programs",   label: "Programs",   icon: FaGraduationCap },
    { to: "trophies",   label: "Trophies",   icon: FaTrophy },
    { to: "skills",     label: "Skills",     icon: FaTools },
    { to: "contact",    label: "Contact",    icon: FaEnvelope },
  ];

  const socialItems = [
    { href: "https://www.linkedin.com/in/akalrashid", icon: FaLinkedin, label: "LinkedIn" },
    { href: "https://github.com/Ahmed-1425/",        icon: FaGithub,   label: "GitHub" },
    { href: "https://x.com/akalrashid",              icon: XIcon,      label: "X" },
  ];

  // ── Mobile bottom-tab items (5 most important) ──
  const mobileNavItems = [
    { to: "home",       label: "Home",       icon: FaHome },
    { to: "about",      label: "About",      icon: FaUser },
    { to: "experience", label: "Experience", icon: FaBriefcase },
    { to: "project",    label: "Projects",   icon: FaProjectDiagram },
    { to: "contact",    label: "Contact",    icon: FaEnvelope },
  ];

  return (
    <>
      {/* ── DESKTOP: left vertical sidebar ── */}
      <nav
        className={`header ${isExpanded && isDesktop ? "expanded" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="logo">
          <Link activeClass="active" to="home" spy smooth duration={30}>
            <div className="logo-img">
              <span className="blink">&lt;</span>
              <span>&#47;</span>AKR<span className="blink">&gt;</span>
            </div>
          </Link>
        </div>

        <ul className="nav-menu">
          {navItems.slice(1).map((item) => (
            <li key={item.to}>
              <Link
                className="navLink"
                activeClass="active"
                to={item.to}
                spy
                smooth
                duration={30}
              >
                <item.icon className="nav-icon" size={22} />
                <span className="nav-label">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="social-links">
          {socialItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <item.icon className="social" size={25} />
              {isExpanded && <span className="social-label">{item.label}</span>}
            </a>
          ))}
        </div>
      </nav>

      {/* ── MOBILE: bottom tab bar ── */}
      {!isDesktop && (
        <nav className={`mobile-tab-bar ${tabVisible ? "mobile-tab-bar--visible" : "mobile-tab-bar--hidden"}`}>
          <div className="mobile-tab-bar__inner">
            {mobileNavItems.map((item) => (
              <Link
                key={item.to}
                className="tab-item"
                activeClass="tab-item--active"
                to={item.to}
                spy
                smooth
                duration={400}
                offset={-20}
              >
                <div className="tab-item__icon-wrap">
                  <item.icon size={22} />
                </div>
                <span className="tab-item__label">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}
    </>
  );
}
